<template>
  <v-container fluid class="centered-tabs mt-3 mx-1" v-show="showComponent">
    <v-form ref="form">
      <v-row>
        <v-col cols="12">
          <h2 class="primary--text font-weight-bold pt-5 pl-2">
            {{ edit ? $t("book/admin:process-base.edit.titles.edit") : $t("book/admin:process-base.edit.titles.add") }}
          </h2>

          <v-card
              elevation="0"
              flat
              tile
          >
            <v-card-text>
              <v-row justify="space-around">
                <v-col cols="12" md="5">
                  <h4 class="primary--text font-weight-medium">{{ $t('common:general') }}</h4>

                  <!-- Name Input -->
                  <v-text-field
                      outlined
                      clearable
                      v-model="process.name"
                      :label="$t('common:name')"
                      :rules="[ruleRequired($t('utils:rules.required'))]"
                      tabindex="1"
                  />

                  <!-- Description Input -->
                  <v-textarea
                      outlined
                      clearable
                      v-model="process.description"
                      :label="$t('common:description')"
                      tabindex="2"
                  />

                  <!-- Available Steps Multiselect -->
                  <v-autocomplete
                      clearable
                      outlined
                      hide-selected
                      multiple
                      :menu-props="{bottom: true, offsetY: true}"
                      :items="restProcessSteps"
                      item-text="name"
                      item-value="id"
                      v-model="process.processSteps"
                      :label="$t('common:process-step_plural')"
                      :rules="[ruleRequiredMultiple($t('utils:rules.multiselect', {name : $t('book/admin:process-base.edit.dialogs.part-4')}))]"
                      tabindex="3"
                      @input="clearSearchTerm"
                      :search-input.sync="stepSearchTerm"
                  >
                    <template #selection="{ item }">
                      <v-chip
                          close
                          @click="itemClicked(item)"
                          @click:close="removeStep(item)"
                      >
                        {{item.name}}
                      </v-chip>
                    </template>
                  </v-autocomplete>

                  <!-- Default Role Select -->
                  <v-select
                      :items="restWorkerRoles"
                      item-text="name"
                      item-value="id"
                      :menu-props="{ bottom: true, offsetY: true }"
                      v-model="process.role"
                      outlined
                      :label="$t('common:role')"
                      :hint="$t('book/admin:process-base.edit.hints.role')"
                      persistent-hint
                      clearable
                      :rules="[ruleRequired($t('utils:rules.required'))]"
                      tabindex="4"
                  />

                  <!-- Additional Section -->
                  <h4 class="primary--text font-weight-medium mt-5">{{ $t('book/admin:process-base.edit.titles.specific') }}</h4>
                  <!-- Checkbox 'Updateable' -->
                  <v-checkbox
                      color="secondary"
                      v-model="process.updateable"
                      :label="$t('book/admin:process-base.edit.labels.check-updateable')"
                      tabindex="5"
                  />

                  <!-- Checkbox 'Old process' -->
                  <v-checkbox
                      color="secondary"
                      v-model="process.addOldResult"
                      :label="$t('book/admin:process-base.edit.labels.check-old-process')"
                      tabindex="6"
                  />
                </v-col>
                <v-col cols="12" md="5">

                  <!-- Management Section -->
                  <h4 class="primary--text font-weight-medium">{{ $t('book/admin:process-base.edit.titles.management') }}</h4>
                  <!-- Start-buffer Input -->
                  <v-text-field
                      outlined
                      clearable
                      v-model="process.startBuffer"
                      :label="$t('book/admin:process-base.edit.labels.start-buffer')"
                      :hint="$t('book/admin:process-base.edit.hints.start-buffer')"
                      persistent-hint
                      type="number"
                      :rules="[ruleRequired($t('utils:rules.required')), ruleNotNegative($t('utils:rules.negative-value'))]"
                      tabindex="7"
                  />

                  <!-- End-buffer Input -->
                  <v-text-field
                      outlined
                      clearable
                      v-model="process.endBuffer"
                      :label="$t('book/admin:process-base.edit.labels.end-buffer')"
                      :hint="$t('book/admin:process-base.edit.hints.end-buffer')"
                      persistent-hint
                      type="number"
                      :rules="[ruleRequired($t('utils:rules.required')), ruleNotNegative($t('utils:rules.negative-value'))]"
                      tabindex="8"
                  />

                  <v-row>
                    <v-col cols="12" lg="6">
                      <!-- Radio buttons for interval -->
                      <v-radio-group v-model="radioGroup" :rules="[ruleRequired($t('utils:rules.required'))]">
                        <v-radio
                            ref="none"
                            color="secondary"
                            :label="$t('book/admin:process-base.edit.labels.radio-no-setting')"
                            value="2"
                        />

                        <v-radio
                            ref="xDays"
                            color="secondary"
                            :label="$t('book/admin:process-base.edit.labels.radio-every-x-days')"
                            value="1"
                        />

                        <v-radio
                            ref="xOfMonth"
                            color="secondary"
                            :label="$t('book/admin:process-base.edit.labels.radio-every-x-of-month')"
                            value="0"
                        />


                      </v-radio-group>
                    </v-col>
                    <v-col cols="12" lg="6" align-self="center">
                      <!-- According Input Fields for above radio buttons -->
                      <v-text-field
                          v-if="radioGroup == '1'"
                          outlined
                          clearable
                          v-model="process.repeatEveryDays"
                          :label="$t('book/admin:process-base.edit.labels.tf-every-x-days')"
                          type="number"
                          :rules="[ruleRequired($t('utils:rules.required'))]"
                      />

                      <v-text-field
                          v-else-if="radioGroup == '0'"
                          outlined
                          clearable
                          v-model="process.repeatEveryMonthOnDay"
                          :label="$t('book/admin:process-base.edit.labels.tf-every-x-of-month')"
                          type="number"
                          :rules="[ruleRequired($t('utils:rules.required')), ruleDaysOfMonth($t('utils:rules.days'))]"
                      />
                    </v-col>
                  </v-row>

                  <!-- Start-Validity Date Picker as Textfield -->
                  <DateField
                      ref="validFrom"
                      @update="(v) => (process.validFrom = v)"
                      :isClearable="true"
                      :label="$t('book/admin:process-base.edit.labels.start-validity')"
                      :hint="$t('book/admin:process-base.edit.hints.start-validity')"
                      tabindex="9"
                  />

                  <DateField
                      ref="validUntil"
                      @update="(v) => (process.validUntil = v)"
                      :isClearable="true"
                      :label="$t('book/admin:process-base.edit.labels.end-validity')"
                      :hint="$t('book/admin:process-base.edit.hints.end-validity')"
                      tabindex="10"
                  />
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12">
          <v-card
              elevation="0"
              flat
              tile
              class="pl-3 pt-3 pb-md-3"
          >
            <BaseButton
                color="primary"
                :btn-text="$t('buttons:save')"
                @clickedButton="updateForm()"
            />

            <TwoButtonDialog
                v-if="edit"
                ref="deleteButton"
                :btn-text="$t('buttons:delete')"
                color="error"
                :dialog-title="$t('dialog:delete-btn-title', {name : $t('book/admin:process-base.edit.dialogs.part-1')})"
                :dialog-text="$t('dialog:delete-btn-dialog', {name : $t('book/admin:process-base.edit.dialogs.part-2')})"
                :button1-text="$t('buttons:cancel')"
                :button2-text="$t('buttons:delete')"
                @clickedButton1="$refs.deleteButton.showDialog = false"
                @clickedButton2="deleteProcess()"
            />

            <DirtyConfirmButtonDialog
                ref="dirtyDialog"
                :btn-text="$t('buttons:back')"
                color="grey"
                :dialog-title="$t('dialog:dirty-save-btn-title')"
                :dialog-text="$t('dialog:dirty-save-btn-dialog')"
                :button1-text="$t('dialog:dirty-save-btn-1-text')"
                :button2-text="$t('dialog:dirty-save-btn-2-text')"
                @clickedButton="$router.go(-1)"
                :dataToWatch="process"
            />
          </v-card>
        </v-col>
      </v-row>
    </v-form>
  </v-container>
</template>

<script>
import Process from "@/modules/book/data/process"
import ProcessStepService from "@/modules/book/services/process/impl/process-step-service"
import ProcessService from "@/modules/book/services/process/impl/process-service"
import {ruleRequired, ruleDaysOfMonth, ruleRequiredMultiple, ruleNotNegative} from "@/modules/core/services/rules"
import Utils from "@/modules/core/services/utils";
import TwoButtonDialog from "@/modules/core/components/TwoButtonDialog/TwoButtonDialog";
import DateField from '@/modules/core/components/DateField';
import WorkerRoleService from "@/modules/core/services/worker-role-service";
import DataUtils from "@/modules/core/services/data-utils";
import ProcessStep from '@/modules/book/data/processStep'
import WorkerRole from "@/modules/core/data/worker-role";
import DirtyConfirmButtonDialog from "@/modules/core/components/TwoButtonDialog/DirtyConfirmButtonDialog";
import BaseButton from "@/modules/core/components/Base/BaseButton";

export default {
  name: "ProcessBaseForm",
  components: {
    TwoButtonDialog,
    DateField,
    DirtyConfirmButtonDialog,
    BaseButton
  },
  data() {
    return {
      process: new Process(),
      valid: false,
      edit: false,

      restProcessSteps: [],
      restWorkerRoles: [],
      radioGroup: '2',

      menuStartValidty: false,
      menuEndValidty: false,
      errorOutline: false,
      isFetchingProcess: true,
      stepSearchTerm: ""
    }
  },
  methods: {
    ruleRequired,
    ruleDaysOfMonth,
    ruleRequiredMultiple,
    ruleNotNegative,

    clearSearchTerm() {
      this.stepSearchTerm = ""
    },

    removeStep(item) {
      const index = this.process.processSteps.indexOf(item.id);
      if (index >= 0) this.process.processSteps.splice(index, 1);
    },

    itemClicked(item) {     
      this.$router.push(`/${this.$store.state.manager.bookModuleUrl}/admin/process/steps/edit/${item.id}`);
    },

    // Process API methods
    updateForm() {

      if (!this.$refs.form.validate()) {
        // TODO scroll to specific form field which doesnt require the rules
        window.scrollTo(0, 0);
        return;
      }

      this.checkRadios();

      if (!this.edit) {
        ProcessService.postToAPI(this.process,
          () => {
            Utils.showSuccessSnackbar(this.$t('snackbar:add-success', {name: this.$t('common:process')}));
            this.$router.go(-1)
          },
          () => {
            Utils.showFailureSnackbar(this.$t('snackbar:add-failure', {name: this.$t('common:process')}));
          }
        );
      } else {
        ProcessService.putToAPI(this.process,
          () => {
            Utils.showSuccessSnackbar(this.$t('snackbar:edit-success', {name: this.$t('common:process')}));
            this.$router.go(-1)
          },
          () => {
            Utils.showFailureSnackbar(this.$t('snackbar:edit-failure', {name: this.$t('common:process')}));
          }
        );
      }
    },

    deleteProcess() {
      this.$refs.deleteButton.setButtonSettings(true, true, false, true);
      ProcessService.deleteFromAPI(this.process,
        () => {
          Utils.showSuccessSnackbar(this.$t('snackbar:delete-success', {name: this.$t('common:process')}));
          this.$router.go(-1)
        },
        () => {
          this.$refs.deleteButton.setButtonSettings(false, false, false, false);
          this.$refs.deleteButton.showDialog = false;
          Utils.showFailureSnackbar(this.$t('snackbar:delete-failure', {name: this.$t('common:process')}));
        }
      )
    },

    // Different API calls
    getAllWorkerRoles() {
      WorkerRoleService.readAllFromAPI(
        (res) => {
          this.restWorkerRoles = DataUtils.parseDataToObjectArray(res.data, WorkerRole);
        },
        (error) => {
          Utils.showFailureSnackbar(this.$t('snackbar:load-failure', {name: this.$t('common:role_plural')}))
          this.$logger.error("Error at getting Worker Roles: " + error);
        }
      )
    },

    getAllProcessSteps() {
      ProcessStepService.readAllFromAPI(
        (res) => {
          this.restProcessSteps = Utils.sort(DataUtils.parseDataToObjectArray(res.data, ProcessStep), false, 'name');
        },
        (error) => {
          Utils.showFailureSnackbar(this.$t('snackbar:load-failure', {name: this.$t('common:process-step_plural')}))
          this.$logger.error("Error at getting Process Steps: " + error);
        }
      )
    },
    checkRadios() {
      if (this.radioGroup == this.$refs.xDays.value) {
        this.process.repeatEveryMonthOnDay = null;
      } else if (this.radioGroup == this.$refs.xOfMonth.value) {
        this.process.repeatEveryDays = null;
      } else if (this.radioGroup == this.$refs.none.value) {
        this.process.repeatEveryDays = null;
        this.process.repeatEveryMonthOnDay = null;
      }
    },
  },
  computed: {
    currentLang() {
      return this.$store.state.translation.language;
    },
    showComponent() {
      if(this.edit) {
        if(this.isFetchingProcess) {
          return false
        } else {
          return true;
        }
      } else {
        return true
      }
    }
  },
  watch: {
    currentLang() {
      Utils.sleep(500).then(() => {
        this.$refs.form.resetValidation()
      });
    }
  },
  created() {
    this.getAllProcessSteps();
    this.getAllWorkerRoles();

    if (this.$route.params.id) {
      this.edit = true;
      ProcessService.readFromAPI(this.$route.params.id,
        (res) => {
          this.process = DataUtils.parseDataToSingleObject(res.data, Process);

          if (this.process.validFrom) {
            this.process.validFrom = this.$refs.validFrom.parseDTO(this.process.validFrom)
          }

          if (this.process.validUntil) {
            this.process.validUntil = this.$refs.validUntil.parseDTO(this.process.validUntil)
          }

          if (this.process.repeatEveryDays != null || this.process.repeatEveryMonthOnDay != null) {
            if (this.process.repeatEveryDays) {
              this.radioGroup = "1";
              this.process.repeatEveryMonthOnDay = null;
            } else if (this.process.repeatEveryMonthOnDay) {
              this.radioGroup = "0";
              this.process.repeatEveryDays = null;
            }
          }
          this.isFetchingProcess = false;
          this.$refs.dirtyDialog.startWatch();
        },
        (error) => {
          this.$logger.error("Error at getting Process: " + error);
          this.$router.push('/404')
        }
      )
    } else {
      this.edit = false;
      this.process.startBuffer = 0;
      this.process.endBuffer = 0;
      this.process.validFrom = null;
      this.process.validUntil = null;
    }
  },
  mounted() {
    this.$refs.dirtyDialog.startWatch();
  }
}
</script>
